import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { ChevronRight, FileCode2, Pencil, Plus, Trash2, Wand2 } from "lucide-react";
import { DataList, FormDialog, type DataListColumn, type FormFieldSpec } from "@ozpanel/kit";
import { ConfirmDialog, Button, Eyebrow, Panel, Section, StateView, cn, useAuth, useToast } from "@ozpanel/ui";

// capabilities.tsx — the module BASELINE. SettingsSpec declares four extra surfaces
// (info_path, raw_path, param_path, maps_path) and ConfigCapability declares resource CRUD
// plus verify/reconfigure. None of them were rendered, so a module page showed the curated
// form and nothing else — no way to see the actual config file, no way to set a directive the
// form does not cover, no way to manage the resources the module owns.
//
// Rendering these generically means every module gains them at once, and a module that adds
// one later gets its UI for free.

// ── Raw configuration files ───────────────────────────────────────────────────────────────
interface RawFile {
  path: string;
  content: string;
}

export function RawConfig({ path }: { path: string }) {
  const { client } = useAuth();
  const raw = useQuery({
    queryKey: ["module-raw", path],
    queryFn: () => client.apiFetch<{ files: RawFile[] }>(path),
  });
  const [open, setOpen] = React.useState<string | null>(null);

  return (
    <StateView loading={raw.isLoading} error={raw.error} errorContext="the raw configuration">
      <div className="space-y-2">
        {(raw.data?.files ?? []).map((f) => {
          const isOpen = open === f.path;
          return (
            <Panel key={f.path} className={cn("overflow-hidden", isOpen && "border-[var(--ozp-accent-line)]")}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : f.path)}
                className="flex w-full items-center gap-2.5 px-4 py-3 text-left hover:bg-surface2"
              >
                <ChevronRight size={13} className={cn("shrink-0 text-faint transition-transform", isOpen && "rotate-90")} />
                <FileCode2 size={14} className="shrink-0 text-accent" />
                <span className="truncate font-mono text-[12.5px]">{f.path}</span>
                <span className="ml-auto shrink-0 font-mono text-[11px] text-faint">
                  {f.content.split("\n").length} lines
                </span>
              </button>
              {isOpen && (
                <pre className="max-h-[520px] overflow-auto border-t border-line bg-bg2 p-4 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap text-muted">
                  {f.content}
                </pre>
              )}
            </Panel>
          );
        })}
      </div>
    </StateView>
  );
}

// ── Read-only info panel ──────────────────────────────────────────────────────────────────
export function InfoPanel({ path, title = "Current state" }: { path: string; title?: string }) {
  const { client } = useAuth();
  const info = useQuery({
    queryKey: ["module-info", path],
    queryFn: () => client.apiFetch<{ items?: { label: string; value: string }[] }>(path),
    // Declared-but-unrouted is a real state on a partially installed node; it must not blow
    // up the whole page, just this panel.
    retry: false,
  });
  if (info.isError) return null;
  const items = info.data?.items ?? [];
  if (!info.isLoading && items.length === 0) return null;

  return (
    <Section title={title} bodyClassName="p-0">
      <StateView loading={info.isLoading} skeleton={<div className="h-20 animate-pulse bg-surface2" />}>
        <div className="divide-y divide-line">
          {items.map((it, i) => (
            <div key={i} className="flex items-baseline justify-between gap-4 px-4 py-2">
              <span className="text-[13px] text-muted">{it.label}</span>
              <span className="text-right font-mono text-[12.5px] break-all">{it.value}</span>
            </div>
          ))}
        </div>
      </StateView>
    </Section>
  );
}

// ── Set any parameter (the escape hatch) ──────────────────────────────────────────────────
export function SetParameter({ path, module }: { path: string; module: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const m = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      client.apiFetch<unknown>(path, { method: "POST", body: JSON.stringify({ key: v.key, value: v.value }) }),
    onSuccess: (_r, v) => {
      notify({ kind: "success", title: `${v.key} set`, body: "Validated and reloaded by the service." });
      void qc.invalidateQueries({ queryKey: ["module-settings", module] });
      void qc.invalidateQueries({ queryKey: ["module-raw"] });
      setOpen(false);
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Wand2 size={13} />
        Set parameter
      </Button>
      <FormDialog
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setError(null); }}
        title="Set any parameter"
        description="For directives the curated form does not cover. The value is validated by the service exactly as a form field would be, and rolled back if it is rejected."
        submitLabel="Set"
        pending={m.isPending}
        error={error}
        fields={[
          { key: "key", label: "Parameter", required: true, help: "The daemon's own directive name." },
          { key: "value", label: "Value", required: true },
        ]}
        onSubmit={(v) => m.mutate(v)}
      />
    </>
  );
}

// ── Resource CRUD (module.Configurable) ───────────────────────────────────────────────────
export interface ConfigColumn { field: string; label: string }
export interface ConfigField {
  name: string;
  label: string;
  type?: string;
  options?: string[];
  placeholder?: string;
  required?: boolean;
  help?: string;
  /** Shown when editing, not changeable. Editing the field an item is addressed BY creates a
   *  second item instead of renaming the first. */
  immutable?: boolean;
}
export interface ConfigResource {
  name: string;
  title: string;
  list_path: string;
  /** The array's key in the list response when it is not "items". */
  items_key?: string;
  create_path?: string;
  item_path?: string;
  /** PUT, same {item} placeholder. */
  update_path?: string;
  /** The collection has no edit, deliberately. Absent update_path alone cannot say this, because
   *  the server falls back to item_path — see module.ConfigResource.NoUpdate. */
  no_update?: boolean;
  item_key?: string;
  columns: ConfigColumn[];
  fields?: ConfigField[];
  danger?: string;
}
export interface ConfigCapability {
  resources: ConfigResource[];
  verify_path?: string;
  reconfigure_path?: string;
}

type Row = Record<string, unknown>;

export function ResourcePanel({ resource }: { resource: ConfigResource }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  // EDITING an existing row. Its absence is why every module with a changeable collection had to
  // hand-build a screen: the generic renderer could add and remove and never change, so mail's
  // relays, aliases and backup jobs and glusterfs' volumes and peers were all unreachable while
  // this component sat one field away from serving them.
  const [editing, setEditing] = React.useState<Row | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<Row | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const key = ["module-resource", resource.list_path];
  const list = useQuery({
    queryKey: key,
    queryFn: () => client.apiFetch<Record<string, unknown>>(resource.list_path),
  });

  const create = useMutation({
    mutationFn: (v: Row) => client.apiFetch<unknown>(resource.create_path!, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => {
      notify({ kind: "success", title: `Added to ${resource.title.toLowerCase()}` });
      void qc.invalidateQueries({ queryKey: key });
      setCreating(false);
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const update = useMutation({
    mutationFn: ({ row, values }: { row: Row; values: Row }) => {
      const target = (resource.update_path || resource.item_path)!.replace(
        "{item}",
        encodeURIComponent(String(row[resource.item_key || "name"])),
      );
      // The row is spread UNDER the edited values, so a field the form does not show is preserved
      // rather than dropped. A PUT is a whole-object replace on most of these routes, and sending
      // only the visible fields is how an option nobody was editing gets switched off with a
      // success toast — the same load-bearing property the vhost options form relies on.
      return client.apiFetch<unknown>(target, {
        method: "PUT",
        body: JSON.stringify({ ...row, ...values }),
      });
    },
    onSuccess: () => {
      notify({ kind: "success", title: "Saved" });
      setEditing(null);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      notify({ kind: "error", title: "Could not save", body: msg });
    },
  });

  const remove = useMutation({
    mutationFn: (row: Row) =>
      client.apiFetch<unknown>(
        resource.item_path!.replace("{item}", encodeURIComponent(String(row[resource.item_key || "name"]))),
        { method: "DELETE" },
      ),
    onSuccess: () => {
      notify({ kind: "success", title: "Removed" });
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => notify({ kind: "error", title: "Could not remove", body: e instanceof Error ? e.message : String(e) }),
  });

  const columns: DataListColumn<Row>[] = [
    ...resource.columns.map((c) => ({
      key: c.field,
      label: c.label,
      sortable: true,
      value: (r: Row) => (r[c.field] === null || r[c.field] === undefined ? "" : String(r[c.field])),
    })),
  ];

  // The rows, from the key the module declares — or "items", or the one array in the payload.
  //
  // Reading "items" and nothing else drew an EMPTY TABLE for /mail/relays, which answers
  // {relays:[…]}. An empty table is the single worst way for this to fail: it is indistinguishable
  // from a collection that genuinely has no rows, so nobody investigates. The sole-array fallback
  // is deliberate and bounded — with two arrays in the payload it gives up rather than guessing,
  // and the module must declare items_key.
  const rows: Row[] = React.useMemo(() => {
    const d = list.data as Record<string, unknown> | undefined;
    if (!d) return [];
    const named = resource.items_key ? d[resource.items_key] : d.items;
    if (Array.isArray(named)) return named as Row[];
    const arrays = Object.values(d).filter(Array.isArray) as Row[][];
    return arrays.length === 1 ? (arrays[0] ?? []) : [];
  }, [list.data, resource.items_key]);

  const toSpec = (f: ConfigField): FormFieldSpec => ({
    key: f.name,
    label: f.label,
    required: f.required,
    placeholder: f.placeholder,
    help: f.help,
    options: f.options?.map((o) => ({ value: o, label: o })),
  });
  const formFields: FormFieldSpec[] = (resource.fields ?? []).map(toSpec);
  // The edit form is the SAME field list, prefilled from the row. One declaration drives both, so
  // a field added for create cannot go missing from edit.
  const editFields: FormFieldSpec[] = (resource.fields ?? []).map((f) => ({
    ...toSpec(f),
    defaultValue: editing ? editing[f.name] : undefined,
  }));

  return (
    <div className="mb-4">
      <div className="mb-3 flex items-center justify-between">
        <Eyebrow accent>{resource.title}</Eyebrow>
        {resource.create_path && formFields.length > 0 && (
          <Button size="sm" variant="soft" onClick={() => setCreating(true)}>
            <Plus size={13} />
            Add
          </Button>
        )}
      </div>
      <DataList
        items={rows}
        columns={columns}
        getRowKey={(r) => String(r[resource.item_key || "name"] ?? JSON.stringify(r))}
        searchable={rows.length > 8}
        loading={list.isLoading}
        error={list.error}
        errorContext={resource.title.toLowerCase()}
        emptyMessage={`No ${resource.title.toLowerCase()} yet.`}
        rowActions={(r) => (
          <div className="flex items-center gap-1">
            {!resource.no_update && (resource.update_path || resource.item_path) && (resource.fields?.length ?? 0) > 0 && (
              <button
                type="button"
                aria-label="Edit"
                onClick={(e) => { e.stopPropagation(); setError(null); setEditing(r); }}
                className="rounded p-1 text-faint hover:bg-surface2 hover:text-fg"
              >
                <Pencil size={13} />
              </button>
            )}
            {resource.item_path && (
              <button
                type="button"
                aria-label="Remove"
                disabled={remove.isPending}
                // A destructive collection asks first. Removing a gluster volume or a mail domain
                // takes data with it, and a single click on a row is how that happens by accident.
                onClick={(e) => {
                  e.stopPropagation();
                  if (resource.danger === "high") setConfirmDelete(r);
                  else remove.mutate(r);
                }}
                className="rounded p-1 text-faint hover:bg-critsoft hover:text-crit disabled:opacity-40"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        )}
      />
      <FormDialog
        open={creating}
        onOpenChange={(o) => { setCreating(o); if (!o) setError(null); }}
        title={`Add to ${resource.title.toLowerCase()}`}
        submitLabel="Add"
        fields={formFields}
        pending={create.isPending}
        error={error}
        onSubmit={(v) => create.mutate(v)}
      />
      {/* EDIT — the same fields as create, prefilled from the row, with the key locked. Editing
          the field an item is addressed BY would silently create a second item instead of renaming
          the first, which is why ConfigField carries `immutable`. */}
      <FormDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) { setEditing(null); setError(null); } }}
        title={`Edit ${resource.title.toLowerCase().replace(/s$/, "")}`}
        submitLabel="Save"
        fields={editFields}
        error={error}
        pending={update.isPending}
        onSubmit={(v) => update.mutate({ row: editing as Row, values: v })}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Remove this ${resource.title.toLowerCase().replace(/s$/, "")}?`}
        confirmLabel="Remove it"
        destructive
        body={
          <p className="text-[12.5px] leading-relaxed">
            <span className="font-mono">
              {confirmDelete ? String(confirmDelete[resource.item_key || "name"] ?? "") : ""}
            </span>{" "}
            will be removed. This collection is marked destructive because removing an entry takes
            something with it that is not recreated by adding the entry back.
          </p>
        }
        onConfirm={() => {
          const r = confirmDelete;
          setConfirmDelete(null);
          if (r) remove.mutate(r);
        }}
      />

    </div>
  );
}

/** Verify / Reconfigure — the two whole-module operations ConfigCapability declares. */
export function ConfigOperations({ cap }: { cap: ConfigCapability }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [result, setResult] = React.useState<unknown>(null);

  const verify = useMutation({
    mutationFn: () => client.apiFetch<unknown>(cap.verify_path!),
    onSuccess: (r) => { setResult(r); notify({ kind: "success", title: "Verified" }); },
    onError: (e) => notify({ kind: "error", title: "Verify failed", body: e instanceof Error ? e.message : String(e) }),
  });
  const reconfigure = useMutation({
    mutationFn: () => client.apiFetch<unknown>(cap.reconfigure_path!, { method: "POST" }),
    onSuccess: (r) => { setResult(r); notify({ kind: "success", title: "Regenerated from records" }); },
    onError: (e) => notify({ kind: "error", title: "Reconfigure failed", body: e instanceof Error ? e.message : String(e) }),
  });

  if (!cap.verify_path && !cap.reconfigure_path) return null;

  return (
    <Section
      title="Consistency"
      actions={
        <div className="flex gap-2">
          {cap.verify_path && (
            <Button size="sm" variant="ghost" disabled={verify.isPending} onClick={() => verify.mutate()}>
              {verify.isPending ? "Checking…" : "Verify"}
            </Button>
          )}
          {cap.reconfigure_path && (
            <Button size="sm" variant="soft" disabled={reconfigure.isPending} onClick={() => reconfigure.mutate()}>
              {reconfigure.isPending ? "Regenerating…" : "Regenerate all"}
            </Button>
          )}
        </div>
      }
    >
      <p className="text-[13px] leading-relaxed text-muted">
        Verify compares this module's records against what is actually on the system. Regenerate
        rewrites every managed file from the records — safe to run, and the fix when the two
        have drifted apart.
      </p>
      {result !== null && (
        <pre className="mt-3 max-h-72 overflow-auto rounded-lg border border-line bg-bg2 p-3 font-mono text-[11.5px] whitespace-pre-wrap text-muted">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </Section>
  );
}

// ── Parameters set outside the curated form ───────────────────────────────────────────────
//
// "Set parameter" was WRITE-ONLY: it could set any directive, and the value then appeared
// nowhere unless it happened to be one of the keys the curated form covers. So an operator
// could change the daemon and afterwards have no way to see what they had changed, or undo it.
//
// This lists every non-default parameter the daemon actually holds that the form does NOT
// cover — reading the module's own raw config (postconf -n, i.e. the daemon, not our records)
// and subtracting the curated keys. That makes the escape hatch inspectable and reversible,
// and incidentally surfaces anything set on the node by hand or by an installer.
export function OtherParameters({
  module,
  rawPath,
  paramPath,
  curatedKeys,
}: {
  module: string;
  rawPath: string;
  paramPath?: string;
  curatedKeys: Set<string>;
}) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<{ key: string; value: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const raw = useQuery({
    queryKey: ["module-raw", rawPath],
    queryFn: () => client.apiFetch<{ files: RawFile[] }>(rawPath),
    retry: false,
  });

  // postconf -n emits `key = value`, with continuation lines indented. Only top-level
  // assignments are parameters.
  const extras = React.useMemo(() => {
    const out: { key: string; value: string }[] = [];
    for (const f of raw.data?.files ?? []) {
      for (const line of f.content.split("\n")) {
        if (!line || /^\s/.test(line)) continue;
        const eq = line.indexOf("=");
        if (eq < 0) continue;
        const key = line.slice(0, eq).trim();
        if (!key || key.startsWith("#") || curatedKeys.has(key)) continue;
        out.push({ key, value: line.slice(eq + 1).trim() });
      }
    }
    return out.sort((a, b) => a.key.localeCompare(b.key));
  }, [raw.data, curatedKeys]);

  const save = useMutation({
    mutationFn: (v: { key: string; value: string }) =>
      client.apiFetch<unknown>(paramPath!, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: (_r, v) => {
      notify({ kind: "success", title: `${v.key} updated` });
      void qc.invalidateQueries({ queryKey: ["module-raw", rawPath] });
      void qc.invalidateQueries({ queryKey: ["module-settings", module] });
      setEditing(null);
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const columns: DataListColumn<{ key: string; value: string }>[] = [
    { key: "key", label: "Parameter", sortable: true },
    { key: "value", label: "Value", sortable: true },
  ];

  return (
    <Section
      collapsible
      defaultOpen={false}
      id="postfix.otherparams"
      hint={`${extras.length} directive${extras.length === 1 ? "" : "s"}`}
      title="Other parameters in force"
      actions={<span className="font-mono text-[11px] text-faint">{extras.length} not in the form above</span>}
      bodyClassName="p-0"
    >
      <div className="p-4">
        <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
          Non-default directives this service is actually running that the curated form does not
          cover — set here with “Set parameter”, by an installer, or by hand on the node. Read
          from the daemon itself, not from our records.
        </p>
        <DataList
          items={extras}
          columns={columns}
          getRowKey={(r) => r.key}
          searchable={extras.length > 8}
          searchPlaceholder="Search parameters…"
          loading={raw.isLoading}
          error={raw.isError ? raw.error : undefined}
          errorContext="the running configuration"
          emptyMessage="Nothing outside the form."
          emptyHint="Every non-default directive this service holds is covered by the fields above."
          rowActions={
            paramPath
              ? (r) => (
                  <button
                    type="button"
                    aria-label={`Edit ${r.key}`}
                    onClick={(e) => { e.stopPropagation(); setError(null); setEditing(r); }}
                    className="rounded p-1 text-faint hover:bg-surface2 hover:text-accent"
                  >
                    <Wand2 size={13} />
                  </button>
                )
              : undefined
          }
        />
      </div>
      {editing && (
        <FormDialog
          open
          onOpenChange={(o) => { if (!o) { setEditing(null); setError(null); } }}
          title={`Edit ${editing.key}`}
          description="Applied through the same validated path as the form — the service checks it and rolls back if it is rejected. Clearing the value sets it empty; it does not restore the built-in default."
          submitLabel="Apply"
          pending={save.isPending}
          error={error}
          fields={[
            { key: "key", label: "Parameter", defaultValue: editing.key, required: true },
            { key: "value", label: "Value", defaultValue: editing.value },
          ]}
          onSubmit={(v) => save.mutate({ key: String(v.key), value: String(v.value ?? "") })}
        />
      )}
    </Section>
  );
}

// ── Lookup tables (Postfix maps) ──────────────────────────────────────────────────────────
interface MapTable { backend: string; path: string; query?: string; dbname?: string; dbuser?: string }
interface MapEntries {
  key: string; label: string; count: number;
  /** File-backed maps can be edited here; DB-backed ones are VIEWS over module data and must
   *  be edited where that data lives, or the two disagree. The server decides, not the UI. */
  editable: boolean;
  managed: boolean; source: string;
  entries: { key: string; value: string }[] | null;
}
interface LookupMap { key: string; label: string; value: string; tables?: MapTable[] }

export function LookupMaps({ crossLink }: { crossLink?: (m: LookupMap) => { label: string; to: string } | null }) {
  const { client } = useAuth();
  const maps = useQuery({
    queryKey: ["postfix-maps"],
    queryFn: () => client.apiFetch<{ maps: LookupMap[] }>("/postfix/maps"),
    retry: false,
  });
  const [open, setOpen] = React.useState<string | null>(null);

  return (
    <Section title="Lookup tables" collapsible defaultOpen={false} id="postfix.maps"
      hint={`${maps.data?.maps?.length ?? 0} tables`} bodyClassName="p-0">
      <div className="p-4">
        <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
          How Postfix answers “is this our domain”, “does this mailbox exist”, “who may send as
          this address”. Each is a live SQL query against the panel database — so the answer
          follows the data you edit elsewhere, with no file to regenerate.
        </p>
        <StateView loading={maps.isLoading} error={maps.isError ? maps.error : undefined} errorContext="the lookup tables">
          <div className="space-y-2">
            {(maps.data?.maps ?? []).map((m) => {
              const isOpen = open === m.key;
              const link = crossLink?.(m) ?? null;
              return (
                <Panel key={m.key} className={cn("overflow-hidden", isOpen && "border-[var(--ozp-accent-line)]")}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : m.key)}
                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left hover:bg-surface2"
                  >
                    <ChevronRight size={12} className={cn("shrink-0 text-faint transition-transform", isOpen && "rotate-90")} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13.5px] font-medium">{m.label}</span>
                      <span className="block truncate font-mono text-[11px] text-faint">{m.key}</span>
                    </span>
                    {m.tables?.[0]?.backend && (
                      <span className="shrink-0 rounded border border-line bg-bg2 px-1.5 py-0.5 font-mono text-[10px] text-muted">
                        {m.tables[0].backend}
                      </span>
                    )}
                  </button>
                  {isOpen && (
                    <div className="space-y-3 border-t border-line bg-bg2 px-4 py-3">
                      {(m.tables ?? []).map((t, i) => (
                        <div key={i}>
                          <Eyebrow className="mb-1">{t.backend} · {t.path}</Eyebrow>
                          {t.query && (
                            <code className="block overflow-x-auto rounded border border-line bg-surface p-2 font-mono text-[11px] whitespace-pre-wrap text-muted">
                              {t.query}
                            </code>
                          )}
                          {t.dbuser && (
                            <p className="mt-1 font-mono text-[10.5px] text-faint">
                              reads {t.dbname} as {t.dbuser}
                            </p>
                          )}
                        </div>
                      ))}
                      {link && (
                        <Link to={link.to} className="inline-block text-[12px] text-accent hover:underline">
                          Edit this data in {link.label} →
                        </Link>
                      )}
                      <MapEntryList mapKey={m.key} />
                    </div>
                  )}
                </Panel>
              );
            })}
          </div>
        </StateView>
      </div>
    </Section>
  );
}

/** MapEntryList — what a lookup table actually RESOLVES right now.
 *
 * The tables panel showed each map's SQL and left the operator to run it themselves to find
 * out what it returns. GET /postfix/maps/{key}/entries answers exactly that and had no UI, as
 * did its add/delete counterparts — three unreached write routes on one module.
 *
 * Live rows are the only way to see that a map is EMPTY when it should not be, which is the
 * failure mode that matters: a sender-login map resolving nothing looks identical to one that
 * is merely unused until mail is refused.
 */
function MapEntryList({ mapKey }: { mapKey: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [adding, setAdding] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const key = ["postfix-map-entries", mapKey];

  const q = useQuery({
    queryKey: key,
    queryFn: () => client.apiFetch<MapEntries>(`/postfix/maps/${encodeURIComponent(mapKey)}/entries`),
    retry: false,
  });

  const add = useMutation({
    mutationFn: (v: Record<string, unknown>) =>
      client.apiFetch(`/postfix/maps/${encodeURIComponent(mapKey)}/entries`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => { notify({ kind: "success", title: "Entry added" }); void qc.invalidateQueries({ queryKey: key }); setAdding(false); setError(null); },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  const del = useMutation({
    mutationFn: (row: { key: string }) =>
      client.apiFetch(`/postfix/maps/${encodeURIComponent(mapKey)}/entries/delete`, { method: "POST", body: JSON.stringify({ key: row.key }) }),
    onSuccess: () => { notify({ kind: "success", title: "Entry removed" }); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => notify({ kind: "error", title: "Could not remove", body: e instanceof Error ? e.message : String(e) }),
  });

  const d = q.data;
  const rows = d?.entries ?? [];
  const columns: DataListColumn<{ key: string; value: string }>[] = [
    { key: "key", label: "Lookup", sortable: true },
    { key: "value", label: "Resolves to", sortable: true },
  ];

  return (
    <div className="mt-3 border-t border-line pt-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <Eyebrow>
          Live entries{d ? ` · ${d.count}` : ""}
          {d && !d.editable && <span className="ml-2 normal-case tracking-normal text-faint">read-only — {d.source}</span>}
        </Eyebrow>
        {d?.editable && (
          <Button size="sm" variant="ghost" onClick={() => { setError(null); setAdding(true); }}>
            <Plus size={12} /> Add
          </Button>
        )}
      </div>
      <DataList
        items={rows}
        columns={columns}
        getRowKey={(r) => r.key}
        searchable={rows.length > 8}
        loading={q.isLoading}
        error={q.isError ? q.error : undefined}
        errorContext="the table's entries"
        emptyMessage="This table resolves nothing."
        emptyHint={
          d?.managed
            ? "It is generated from panel data — an empty table usually means the underlying records are missing, not that the map is broken."
            : undefined
        }
        rowActions={d?.editable ? (r) => (
          <button type="button" aria-label={`Remove ${r.key}`} disabled={del.isPending}
            onClick={(e) => { e.stopPropagation(); del.mutate(r); }}
            className="rounded p-1 text-faint hover:bg-critsoft hover:text-crit disabled:opacity-40">
            <Trash2 size={12} />
          </button>
        ) : undefined}
      />
      {adding && (
        <FormDialog
          open
          onOpenChange={(o) => { if (!o) { setAdding(false); setError(null); } }}
          title={`Add to ${d?.label ?? mapKey}`}
          description="File-backed table. The map is re-hashed after the write — editing the file alone would change nothing until postmap runs."
          submitLabel="Add entry"
          pending={add.isPending}
          error={error}
          fields={[
            { key: "key", label: "Lookup key", required: true },
            { key: "value", label: "Resolves to", required: true },
          ]}
          onSubmit={(v) => add.mutate(v)}
        />
      )}
    </div>
  );
}
